import { ChevronRight, Copy, Landmark, Plus } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { formatAccountGroups } from "../services/depositAccounts.service";
import type { DepositAccountRow, DepositAccountsCorridor } from "../types/depositAccounts.types";

export interface DepositAccountsListProps {
  corridor: DepositAccountsCorridor;
  accounts: DepositAccountRow[];
  isLoading: boolean;
  onSelect: (account: DepositAccountRow) => void;
  onRegister: () => void;
}

/** `metadata.note` when it's a string; the drawer renders the full metadata object. */
function accountNote(account: DepositAccountRow): string | null {
  const note = account.metadata?.["note"];
  return typeof note === "string" && note.trim().length > 0 ? note : null;
}

/**
 * The org's registered deposit receiving accounts as cards (low volume — no pagination, the endpoint returns
 * the full set). Read-only by design: there is NO edit/delete/deactivate endpoint, so no such affordances
 * exist. Client-side quick filter over the account digits and note. Generic over the corridor (Mexico CLABEs
 * / Argentina CVUs) — only the i18n namespace, digit grouping and the optional flavor badge differ.
 */
export function DepositAccountsList({
  corridor,
  accounts,
  isLoading,
  onSelect,
  onRegister,
}: DepositAccountsListProps) {
  const { t } = useTranslations(corridor.i18nNamespace);
  const [filter, setFilter] = useState("");

  const copyAccount = async (account: string) => {
    try {
      await navigator.clipboard.writeText(account);
      colocaToast.success(t("copy_success"));
    } catch {
      // Clipboard may be unavailable — silently skip.
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <ColocaSkeleton className="h-5 w-64 max-w-full" />
            <ColocaSkeleton className="mt-2 h-3.5 w-40" />
          </div>
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <ColocaEmpty icon={Landmark} title={t("empty_title")} description={t("empty_description")}>
        <ColocaButton variant="primary" icon={Plus} onClick={onRegister}>
          {t("button_register")}
        </ColocaButton>
      </ColocaEmpty>
    );
  }

  const normalizedFilter = filter.trim().toLowerCase();
  // A text-only filter yields no digits — matching `includes("")` would be always-true, so the account
  // digit match only applies when the filter actually contains digits.
  const filterDigits = normalizedFilter.replace(/\D/g, "");
  const visible = normalizedFilter
    ? accounts.filter(
        (a) =>
          (filterDigits.length > 0 && a.account.includes(filterDigits)) ||
          (accountNote(a) ?? "").toLowerCase().includes(normalizedFilter),
      )
    : accounts;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("list_title")} ({accounts.length})
        </p>
        <ColocaSearchInput
          value={filter}
          onValueChange={setFilter}
          placeholder={t("filter_placeholder")}
          className="w-full sm:max-w-56"
        />
      </div>

      <ul className="space-y-2">
        {visible.map((account) => {
          const note = accountNote(account);
          return (
            <li key={account.id}>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  // Clicks on interactive children (the copy button) must not also open the drawer.
                  if (e.target instanceof Element && e.target.closest("button")) return;
                  onSelect(account);
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                    e.preventDefault();
                    onSelect(account);
                  }
                }}
                className="group flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
              >
                {/* Status dot — text + shape, not color alone */}
                <span
                  aria-hidden="true"
                  className={
                    account.is_active
                      ? "size-2 shrink-0 rounded-full bg-success"
                      : "size-2 shrink-0 rounded-full bg-muted-foreground"
                  }
                />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-base font-semibold tracking-wide text-foreground tabular-nums">
                      {formatAccountGroups(account.account, corridor.digitCount)}
                    </span>
                    <ColocaIconButton
                      icon={Copy}
                      variant="ghost"
                      size="sm"
                      aria-label={t("copy_aria")}
                      onClick={() => {
                        void copyAccount(account.account);
                      }}
                    />
                    {account.flavor && (
                      <ColocaBadge variant="secondary" className="text-[10px]">
                        {account.flavor === "dynamic" ? t("flavor_dynamic") : t("flavor_static")}
                      </ColocaBadge>
                    )}
                    <ColocaBadge variant={account.is_active ? "success" : "default"} className="text-[10px]">
                      {account.is_active ? t("status_active") : t("status_inactive")}
                    </ColocaBadge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("registered_at")} {formatDateTime(account.created_at)}
                    {note ? ` · ${t("note_label")}: ${note}` : ""}
                  </p>
                </div>

                <ChevronRight
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </div>
            </li>
          );
        })}
      </ul>

      {visible.length === 0 && (
        <p className="py-2 text-center text-sm text-muted-foreground">{t("filter_no_results")}</p>
      )}
    </div>
  );
}
